import React, { useEffect, useState } from "react";
import { Container, Card, Modal, Button } from "react-bootstrap";
import axios from "axios";
import { useTranslation } from "react-i18next";
import "../css/CampaignSection.css";
import { formatDate } from "../helpers/dateFormat";

const CampaignSection = () => {
  const { t, i18n } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchCampaigns = async () => {
      const cacheKey = "campaign_cache_v1";
      try {
        setLoading(true);
        const cachedRaw = sessionStorage.getItem(cacheKey);
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw);
          setCampaigns(cached);
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_APP_URL}/promos`,
          {
            headers: {
              "X-Platform": process.env.REACT_APP_X_PLATFORM,
              "API-Key": process.env.REACT_APP_API_KEY,
              "X-API-Version": process.env.REACT_APP_X_API_VERSION,
            },
            withCredentials: true,
          },
        );

        const items = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        if (!cancelled) {
          setCampaigns(items);
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(items));
          } catch {}
        }
      } catch (err) {
        console.error("Error fetching campaign data:", err);
        if (!cancelled) setCampaigns([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCampaigns();

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const imageUrl = (imagePath) => {
    if (!imagePath)
      return `${process.env.PUBLIC_URL || ""}/assets/placeholder.jpg`;
    return `${(process.env.REACT_APP_STORAGE_URL || "").replace(/\/$/, "")}/${imagePath.replace(/^\//, "")}`;
  };

  const handleCardClick = (campaign) => {
    setSelectedCampaign(campaign);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCampaign(null);
  };

  if (loading) {
    return (
      <Container className="campaign-section py-5">
        <div className="campaign-loading-container">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div className="campaign-loading-item" key={`loading-${idx}`}>
              <Card className="shadow-lg rounded-4 campaign-card">
                <div className="campaign-loading-img"></div>
                <Card.Body className="p-3">
                  <div className="campaign-loading-title"></div>
                  <div className="campaign-loading-line"></div>
                  <div
                    className="campaign-loading-line"
                    style={{ width: "80%" }}
                  ></div>
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      </Container>
    );
  }

  const duplicatedCampaigns =
    campaigns.length > 1 ? [...campaigns, ...campaigns] : campaigns;
  const trackWidth = duplicatedCampaigns.length * (320 + 30);

  return (
    <section id="campaign" className="py-5">
      <Container>
        <h2 className="text-center fw-bold mb-4">
          {t("descriptionPromos") || "Promos"}
        </h2>

        <div className="campaign-marquee">
          <div className="campaign-track" style={{ width: `${trackWidth}px` }}>
            {duplicatedCampaigns.map((campaign, idx) => {
              const uniqueKey = `${campaign.id ?? campaign.title ?? idx}-${idx}`;
              return (
                <div
                  className="campaign-item"
                  key={uniqueKey}
                  onClick={() => handleCardClick(campaign)}
                >
                  <Card className="shadow-lg rounded-4 campaign-card">
                    <Card.Img
                      variant="top"
                      src={imageUrl(campaign.image)}
                      alt={campaign.title}
                      className="campaign-img"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/assets/placeholder.jpg`;
                      }}
                    />
                    <Card.Body className="p-3">
                      <Card.Title className="fw-bold">
                        {campaign.title}
                      </Card.Title>
                      <Card.Text className="text-muted">
                        {campaign.description}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </Container>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        fullscreen="md-down"
        dialogClassName="responsive-modal"
      >
        {selectedCampaign && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{selectedCampaign.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <img
                src={imageUrl(selectedCampaign.image)}
                alt={selectedCampaign.title}
                className="img-fluid rounded mb-3"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/assets/placeholder.jpg`;
                }}
              />
              <p>{selectedCampaign.description}</p>

              {selectedCampaign.discount && (
                <p>
                  <strong>Diskon: </strong>
                  {selectedCampaign.discount}%
                </p>
              )}
              {selectedCampaign.start_date && selectedCampaign.end_date && (
                <p>
                  <strong>Periode: </strong>
                  {formatDate(selectedCampaign.start_date)} -{" "}
                  {formatDate(selectedCampaign.end_date)}
                </p>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </section>
  );
};

export default CampaignSection;
